<template>
  <div class="post-detail-container">
    <!-- 返回按钮 -->
    <el-button icon="el-icon-arrow-left" class="back-btn" @click="goBack">返回</el-button>

    <!-- 帖子标题 -->
    <h1 class="post-title">{{ post.title }}</h1>

    <!-- 作者信息 -->
    <div class="author-info">
      <el-avatar 
        :src="post.author.avatar" 
        class="author-avatar"
        @click.native="showUserProfile(post.author)"
      ></el-avatar>
      <div class="author-details">
        <span class="author-name">{{ post.author.name }}</span>
        <span class="post-time">{{ post.createdAt }}</span>
      </div>
    </div>

    <!-- 帖子内容 -->
    <div class="post-content">
      <p>{{ post.content }}</p>
    </div>

    <!-- 图片展示 -->
    <div v-if="post.images && post.images.length > 0" class="post-images">
      <el-image
        v-for="(image, index) in post.images"
        :key="index"
        :src="image"
        :preview-src-list="post.images"
        class="post-image"
      ></el-image>
    </div>

    <!-- 操作栏 -->
    <div class="action-bar">
      <el-button type="text" @click="handlePostLike">
        <i class="el-icon-thumb" :style="{ color: isLiked ? '#409EFF' : '' }"></i> 
        {{ post.likes }} {{ isLiked ? '已赞' : '点赞' }}
      </el-button>
      <el-button type="text" @click="handleReport()">
        <i class="el-icon-warning"></i> 举报帖子
      </el-button>
      <el-button type="text" @click="handleCollect">
        <i class="el-icon-star-off" :style="{ color: isCollected ? '#409EFF' : '' }"></i> 
        {{ post.collects }} {{ isCollected ? '已收藏' : '收藏' }}
      </el-button>
    </div>

    <!-- 评论区 -->
    <div class="comment-section">
      <h3>评论 ({{ totalComments }})</h3>
      <div class="comment-list">
        <!-- 显示可见评论 -->
        <div v-for="comment in visibleComments" :key="comment.id" class="comment-item">
          <el-avatar
            :src="comment.user.avatar"
            class="comment-avatar"
            @click.native="showUserProfile(comment.user)"
          ></el-avatar>
          <div class="comment-main">
            <div class="comment-header">
              <span class="comment-user" @click="showUserProfile(comment.user)">
                {{ comment.user.name }}
              </span>
              <span class="comment-time">{{ comment.createdAt }}</span>
            </div>
            <div class="comment-text">{{ comment.content }}</div>
            <div class="comment-actions">
              <el-button type="text" @click="checkLogin(() => toggleReply(comment.id))">
                <i class="el-icon-chat-line-round"></i> 回复
              </el-button>
              <el-button type="text" @click="checkLogin(() => handleLike(comment))">
                <i class="el-icon-thumb"></i> {{ comment.likes || 0 }} 点赞
              </el-button>
              <el-button type="text" @click="handleReport(comment)">
                <i class="el-icon-warning"></i> 举报
              </el-button>
            </div>
            <div v-if="activeReplyId === comment.id" class="reply-input">
              <el-input
                v-model="replyContent"
                type="textarea"
                :rows="2"
                placeholder="写下你的回复..."
              ></el-input>
              <div class="reply-buttons">
                <el-button size="small" @click="cancelReply">取消</el-button>
                <el-button type="primary" size="small" @click="submitReply(comment.id)">
                  提交
                </el-button>
              </div>
            </div>
            <div v-if="comment.replies && comment.replies.length" class="reply-list">
              <div v-for="reply in comment.replies" :key="reply.id" class="reply-item">
                <el-avatar
                  :src="reply.user.avatar"
                  size="small"
                  @click.native="showUserProfile(reply.user)"
                ></el-avatar>
                <div class="reply-content">
                  <span class="reply-user" @click="showUserProfile(reply.user)">
                    {{ reply.user.name }}
                  </span>
                  <span class="reply-text">{{ reply.content }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 显示更多/收起评论按钮 -->
        <div v-if="post.comments.length > 5" class="show-more-comments">
          <el-button 
            type="text" 
            @click="toggleShowAllComments"
            :icon="showAllComments ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
          >
            {{ showAllComments ? '收起评论' : `展开更多评论(${post.comments.length - 5})` }}
          </el-button>
        </div>
      </div>
      
      <!-- 评论输入区域 -->
      <div class="comment-input-area">
        <el-input
          v-model="newComment"
          type="textarea"
          placeholder="写下你的评论..."
          :rows="3"
          class="comment-input"
          @focus="checkLogin"
        ></el-input>
        <el-button 
          type="primary" 
          @click="submitComment"
          :disabled="!isLoggedIn"
        >
          提交评论
        </el-button>
      </div>
    </div>

    <!-- 举报弹窗 -->
    <el-dialog
      title="选择举报原因"
      :visible.sync="reportDialogVisible"
      width="500px"
      @close="handleReportClose"
    >
      <el-checkbox-group 
        v-model="selectedReports"
        :max="2"
        class="report-reasons"
      >
        <el-checkbox 
          v-for="reason in reportReasons" 
          :key="reason.id"
          :label="reason.id"
          class="report-reason-item"
        >
          {{ reason.label }}
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer" class="dialog-footer">
        <el-button @click="reportDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitReport">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 登录提示弹窗 -->
    <el-dialog
      title="提示"
      :visible.sync="loginDialogVisible"
      width="30%"
    >
      <span>您尚未登录，是否前往登录？</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="loginDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="goToLogin">前往登录</el-button>
      </span>
    </el-dialog>

    <!-- 用户信息弹窗 -->
    <el-dialog :visible.sync="userProfileVisible" title="用户信息" width="30%">
      <div v-if="selectedUser" class="user-profile">
        <el-avatar :src="selectedUser.avatar" :size="80"></el-avatar>
        <h3>{{ selectedUser.name }}</h3>
        <p v-if="selectedUser.bio">{{ selectedUser.bio }}</p>
        <div class="user-stats">
          <span>粉丝：{{ selectedUser.followers || 0 }}</span>
          <span>关注：{{ selectedUser.following || 0 }}</span>
        </div>
        <div>
          <el-button 
            v-if="!isAttention" 
            type="primary" 
            class="el-icon-plus" 
            @click="checkLogin(beAttention)"
          >关注</el-button>
          <el-button 
            v-if="isAttention" 
            type="success" 
            class="el-icon-check" 
            plain 
            @click="cancelAttention"
          >已关注</el-button>
          <el-button 
            type="info" 
            class="el-icon-message" 
            @click="checkLogin(sendMessage)"
          >私信</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      reportDialogVisible: false,
      selectedReports: [],
      reportTarget: null,
      reportReasons: [
        { id: 1, label: '垃圾广告或营销信息' },
        { id: 2, label: '色情或低俗内容' },
        { id: 3, label: '人身攻击或辱骂' },
        { id: 4, label: '违法或违规内容' },
        { id: 5, label: '不实信息' },
        { id: 6, label: '侵犯隐私' },
        { id: 7, label: '其他原因' }
      ],
      isLiked: false,
      isCollected: false,
      isAttention: false,
      showAllComments: false,
      isLoggedIn: false, // 实际项目中应该从store或localStorage获取
      loginDialogVisible: false,
      post: {
        id: 1,
        title: "实现展示页面",
        content: "This is a test Model",
        author: {
          id: 1,
          name: "Demo",
          avatar: "https://avatars.githubusercontent.com/u/12345678?v=4",
          bio: "这是一个示例用户",
          followers: 123,
          following: 45
        },
        likes: 123,
        collects: 45,
        comments: [
          {
            id: 1,
            user: {
              id: 2,
              name: "用户A",
              avatar: "https://avatars.githubusercontent.com/u/23456789?v=4",
            },
            content: "1",
            likes: 5,
            replies: [
              {
                id: 11,
                user: {
                  id: 4,
                  name: "用户C",
                  avatar: "https://avatars.githubusercontent.com/u/45678901?v=4",
                },
                content: "1-1",
                createdAt: "2023-10-01 11:00",
              },
            ],
            createdAt: "2023-10-01 10:30",
          },
          {
            id: 2,
            user: {
              id: 3,
              name: "用户B",
              avatar: "https://avatars.githubusercontent.com/u/34567890?v=4",
            },
            content: "2",
            likes: 2,
            replies: [],
            createdAt: "2023-10-01 10:35",
          },
          {
            id: 3,
            user: {
              id: 4,
              name: "用户C",
              avatar: "https://avatars.githubusercontent.com/u/45678901?v=4",
            },
            content: "3",
            likes: 0,
            replies: [],
            createdAt: "2023-10-01 10:40",
          },
          {
            id: 4,
            user: {
              id: 5,
              name: "用户D",
              avatar: "https://avatars.githubusercontent.com/u/56789012?v=4",
            },
            content: "4",
            likes: 1,
            replies: [],
            createdAt: "2023-10-01 10:45",
          },
          {
            id: 5,
            user: {
              id: 6,
              name: "用户E",
              avatar: "https://avatars.githubusercontent.com/u/67890123?v=4",
            },
            content: "5",
            likes: 3,
            replies: [],
            createdAt: "2023-10-01 10:50",
          },
          {
            id: 6,
            user: {
              id: 7,
              name: "用户F",
              avatar: "https://avatars.githubusercontent.com/u/78901234?v=4",
            },
            content: "6",
            likes: 0,
            replies: [],
            createdAt: "2023-10-01 10:55",
          },
        ],
        images: ["https://avatars.githubusercontent.com/u/45678901?", "https://avatars.githubusercontent.com/u/401?"],
        createdAt: "2023-10-01 10:00",
      },
      newComment: "",
      activeReplyId: null,
      replyContent: "",
      userProfileVisible: false,
      selectedUser: null,
    };
  },
  computed: {
    totalComments() {
      return this.post.comments.reduce((total, comment) => {
        return total + 1 + (comment.replies?.length || 0);
      }, 0);
    },
    visibleComments() {
      return this.showAllComments 
        ? this.post.comments 
        : this.post.comments.slice(0, 5);
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    handlePostLike() {
      if (!this.checkLogin()) return;
      
      this.isLiked = !this.isLiked;
      this.post.likes += this.isLiked ? 1 : -1;
      this.$message.success(this.isLiked ? "点赞成功！" : "取消点赞！");
    },
    handleCollect() {
      if (!this.checkLogin()) return;
      
      this.isCollected = !this.isCollected;
      this.post.collects += this.isCollected ? 1 : -1;
      this.$message.success(this.isCollected ? "收藏成功！" : "取消收藏！");
    },
    submitComment() {
      if (!this.checkLogin()) return;
      
      if (!this.newComment.trim()) {
        this.$message.warning("评论内容不能为空！");
        return;
      }
      
      this.post.comments.push({
        id: Date.now(),
        user: {
          id: 4,
          name: "当前用户",
          avatar: "https://avatars.githubusercontent.com/u/45678901?v=4",
        },
        content: this.newComment,
        likes: 0,
        replies: [],
        createdAt: new Date().toLocaleString(),
      });
      
      this.newComment = "";
      this.$message.success("评论成功！");
    },
    toggleReply(commentId) {
      this.activeReplyId = this.activeReplyId === commentId ? null : commentId;
      this.replyContent = "";
    },
    submitReply(commentId) {
      if (!this.replyContent.trim()) {
        this.$message.warning("回复内容不能为空！");
        return;
      }
      
      const comment = this.post.comments.find(c => c.id === commentId);
      if (comment) {
        comment.replies.push({
          id: Date.now(),
          user: {
            id: 4,
            name: "当前用户",
            avatar: "https://avatars.githubusercontent.com/u/45678901?v=4",
          },
          content: this.replyContent,
          createdAt: new Date().toLocaleString(),
        });
        
        this.replyContent = "";
        this.activeReplyId = null;
        this.$message.success("回复成功！");
      }
    },
    cancelReply() {
      this.activeReplyId = null;
      this.replyContent = "";
    },
    showUserProfile(user) {
      this.selectedUser = user;
      this.userProfileVisible = true;
    },
    handleReport(target) {
      this.reportTarget = target;
      this.reportDialogVisible = true;
    },
    submitReport() {
      if (this.selectedReports.length === 0) {
        this.$message.warning('请至少选择一个举报原因');
        return;
      }
      
      const selectedReasonText = this.reportReasons
        .filter(r => this.selectedReports.includes(r.id))
        .map(r => r.label)
        .join('，');

      const reportData = {
        targetType: this.reportTarget ? 'comment' : 'post',
        targetId: this.reportTarget?.id || this.post.id,
        reasons: this.selectedReports
      };

      console.log('提交举报数据:', reportData);
      
      // 模拟API调用
      setTimeout(() => {
        this.$message.success(`举报已提交，原因：${selectedReasonText}`);
        this.reportDialogVisible = false;
      }, 500);
    },
    handleReportClose() {
      this.selectedReports = [];
      this.reportTarget = null;
    },
    beAttention() {
      this.isAttention = true;
      this.$message.success("关注成功！");
    },
    cancelAttention() {
      this.isAttention = false;
      this.$message.success("已取消关注！");
    },
    sendMessage() {
      this.$router.push('/mesPage');
    },
    toggleShowAllComments() {
      this.showAllComments = !this.showAllComments;
    },
    checkLogin(callback) {
      if (!this.isLoggedIn) {
        this.loginDialogVisible = true;
        return false;
      }
      if (callback && typeof callback === 'function') {
        callback();
      }
      return true;
    },
    goToLogin() {
      this.loginDialogVisible = false;
      this.$router.push('/loginPage');
    },
    handleLike(comment) {
      if (!this.checkLogin()) return;
      
      if (!comment.likes) {
        this.$set(comment, 'likes', 0);
      }
      
      if (comment.isLiked) {
        comment.likes--;
        comment.isLiked = false;
      } else {
        comment.likes++;
        comment.isLiked = true;
      }
    }
  },
  created() {
    // 初始化评论的点赞状态
    this.post.comments.forEach(comment => {
      this.$set(comment, 'isLiked', false);
    });
  }
};
</script>

<style scoped>
.report-reasons {
  display: flex;
  flex-direction: column;
}

.report-reason-item {
  display: block;
  margin: 8px 0;
}

::v-deep .el-checkbox__label {
  white-space: normal;
  line-height: 1.5;
}

.post-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

.back-btn {
  position: absolute;
  left: 20px;
  top: 20px;
  padding: 8px 15px;
}

.post-title {
  font-size: 24px;
  font-weight: bold;
  margin: 40px 0 20px;
  text-align: center;
}

.author-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.author-avatar {
  margin-right: 10px;
  cursor: pointer;
}

.author-details {
  display: flex;
  flex-direction: column;
}

.author-name {
  font-size: 16px;
  font-weight: bold;
}

.post-time {
  font-size: 14px;
  color: #999;
}

.post-content {
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f8f8;
  border-radius: 4px;
}

.post-images {
  display: grid;
  gap: 10px;
  margin-bottom: 20px;
}

.post-image {
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
}

.action-bar {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 30px 0;
  padding: 15px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.comment-section {
  margin-top: 30px;
}

.comment-list {
  margin: 20px 0;
}

.comment-item {
  display: flex;
  margin: 15px 0;
  padding: 15px;
  background: #f8f8f8;
  border-radius: 4px;
}

.comment-avatar {
  margin-right: 10px;
  cursor: pointer;
}

.comment-main {
  flex: 1;
}

.comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.comment-user {
  font-weight: bold;
  margin-right: 8px;
  cursor: pointer;
  color: #409eff;
}

.comment-time {
  font-size: 12px;
  color: #999;
}

.comment-text {
  font-size: 14px;
  line-height: 1.5;
}

.comment-actions {
  margin-top: 10px;
}

.reply-list {
  margin-left: 30px;
  border-left: 2px solid #ddd;
  padding-left: 15px;
}

.reply-item {
  display: flex;
  align-items: center;
  margin: 10px 0;
}

.reply-content {
  margin-left: 10px;
}

.reply-user {
  font-weight: 500;
  margin-right: 8px;
  cursor: pointer;
  color: #409eff;
}

.reply-text {
  font-size: 14px;
}

.comment-input-area {
  margin: 20px 0;
}

.comment-input {
  margin-bottom: 10px;
}

.show-more-comments {
  text-align: center;
  margin: 15px 0;
}

.user-profile {
  text-align: center;
}

.user-profile h3 {
  margin: 15px 0;
}

.user-stats {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 15px 0;
  color: #666;
}

.el-button + .el-button {
  margin-left: 10px;
}

.reply-input {
  margin-top: 10px;
}

.reply-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
</style>